import { useDebounceFn } from "@vueuse/core";

interface DocumentObserver {
    subscribe: (callback: () => void) => void;
    unsubscribe: (callback: () => void) => void;
}

const observers = new Set<VoidFunction>();
const resizeObserver = new ResizeObserver(handleResize);
const mutationObserver = new MutationObserver(handleMutations);

let isObserving = false;

function handleResize(): void {
    requestUpdate();
}

function handleMutations(): void {
    requestUpdate();
}

const debouncedRequestUpdate = useDebounceFn(() => {
    observers.forEach((callback) => callback());
}, 16);

function requestUpdate(): void {
    debouncedRequestUpdate();
}

export function useDocumentObserver(): DocumentObserver {
    function startObserving(): void {
        if (isObserving) return;

        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });

        resizeObserver.observe(document.body);
        isObserving = true;
    }

    function stopObserving(): void {
        if (!isObserving) return;

        mutationObserver.disconnect();
        resizeObserver.disconnect();
        isObserving = false;
    }

    function subscribe(callback: () => void): void {
        observers.add(callback);
        if (!isObserving) {
            startObserving();
        }
    }

    function unsubscribe(callback: () => void): void {
        observers.delete(callback);
        if (observers.size === 0) {
            stopObserving();
        }
    }

    return {
        subscribe,
        unsubscribe,
    };
}
